/**
 * The quick look: one small window, above any page, with the chart of one
 * symbol (`components/chart/ChartQuickLook.tsx` draws it).
 *
 * The owner, 2026-10-14, of The Week in Earnings: "when i click chart,
 * nothing shows up. can you have a small window that is the chart when we
 * click chart so we can have a quick glimpse?" The card's Chart button asked
 * an Advanced Chart on the same page to take the symbol
 * (`chartSymbolRequest.ts`), and his Outlook has no chart.
 *
 * Which window is open is this module's, not a pane's: the button that opens
 * it sits in a card that closes as it is pressed (the Outlook's agenda even
 * mounts the card again under a new key), so the state cannot live in the
 * card or in the pane that holds it. One host, mounted once beside the
 * desks' keep-alive host (`PrefsSyncGate`), draws whatever this says.
 *
 * No React, no chart: a list imports `openChartQuickLook` and carries
 * nothing of the chart until the window opens.
 */

/** The chart's own symbol scope. No page, list or other chart shares it, so
 *  the window's chart never moves another and is never moved by one. */
export const CHART_QUICK_LOOK_SCOPE = "chart-quick-look";

/** The saved chart layout every opening takes as its template: its
 *  indicators, interval and look, never its ticker or drawings (the owner,
 *  2026-10-15: "everytime a chart is open, use the scanner - standard
 *  template"). Matched as the Scanner matches it (`layoutNamed`); with no
 *  layout of that name saved, the chart keeps its own. */
export const CHART_QUICK_LOOK_TEMPLATE = "Scanner - Standard";

export type ChartQuickLookState = {
  symbol: string;
  name: string | null;
  /** Counts presses, so a second press on the same symbol shows it again. */
  seq: number;
};

let state: ChartQuickLookState | null = null;
let seq = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

/** The ticker as every chart store writes it. */
export function quickLookSymbol(symbol: string | null | undefined): string {
  return String(symbol ?? "").trim().toUpperCase().replace(/\.US$/i, "");
}

/** What the window holds, said on hover of every button that opens it. */
export function chartQuickLookTitle(symbol: string): string {
  return `Open a small window with the chart of ${quickLookSymbol(symbol)}`;
}

/** Open the window on `symbol` (replacing whatever it showed). A blank ticker does nothing. */
export function openChartQuickLook(symbol: string | null | undefined, name?: string | null): void {
  const sym = quickLookSymbol(symbol);
  if (!sym) return;
  seq += 1;
  const label = (name ?? "").trim();
  state = { symbol: sym, name: label && label.toUpperCase() !== sym ? label : null, seq };
  emit();
}

export function closeChartQuickLook(): void {
  if (!state) return;
  state = null;
  emit();
}

/** For `useSyncExternalStore`. */
export function subscribeChartQuickLook(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** For `useSyncExternalStore`: the same object until the next open or close. */
export function chartQuickLookNow(): ChartQuickLookState | null {
  return state;
}
